import { ProcessingError } from './errors';

export const PROCESSING_STAGES = [
  'queued',
  'loading-engine',
  'reading-input',
  'inspecting',
  'rendering',
  'extracting',
  'transforming',
  'ocr',
  'model-inference',
  'writing-output',
  'validating-output',
  'finalizing',
  'completed',
] as const;

export type ProcessingStage = (typeof PROCESSING_STAGES)[number];
export type ProcessingProgressUnit = 'bytes' | 'files' | 'items' | 'pages' | 'tokens';

export type ProcessingProgressUpdate =
  | {
      readonly kind: 'indeterminate';
      readonly stage: ProcessingStage;
      /** Optional localization key for a more precise engine stage. */
      readonly detailCode?: string;
    }
  | {
      readonly kind: 'counted';
      readonly stage: ProcessingStage;
      readonly completed: number;
      readonly total: number;
      readonly unit: ProcessingProgressUnit;
      readonly detailCode?: string;
    };

export type ProcessingProgressEvent = ProcessingProgressUpdate & {
  readonly jobId: string;
  readonly sequence: number;
};

export interface ProgressReporter {
  report(update: ProcessingProgressUpdate): void;
}

const STAGE_SET = new Set<string>(PROCESSING_STAGES);
const UNIT_SET = new Set<string>(['bytes', 'files', 'items', 'pages', 'tokens']);

export function assertProgressUpdate(
  update: ProcessingProgressUpdate,
): asserts update is ProcessingProgressUpdate {
  if (!STAGE_SET.has(update.stage)) {
    throw new ProcessingError('processing.invalidProgress', { reason: 'stage' });
  }
  if (update.kind === 'indeterminate') return;
  if (
    !UNIT_SET.has(update.unit) ||
    !Number.isSafeInteger(update.completed) ||
    !Number.isSafeInteger(update.total) ||
    update.completed < 0 ||
    update.total <= 0 ||
    update.completed > update.total
  ) {
    throw new ProcessingError('processing.invalidProgress', { reason: 'count' });
  }
}

export function createProgressReporter(
  jobId: string,
  emit: (event: ProcessingProgressEvent) => void,
): ProgressReporter {
  let sequence = 0;
  return {
    report(update) {
      assertProgressUpdate(update);
      sequence += 1;
      emit({ ...update, jobId, sequence });
    },
  };
}

